import * as React from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Circle } from 'lucide-react';
import type { VariantProps } from 'class-variance-authority';

import { cn } from '@repo/ui/lib/utils';
import { Button, buttonVariants } from './button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from './empty';
import { LiquidSurface } from './liquid-surface';
import type { StatusTone } from './status-badge';

interface StatePanelItem {
  label: string;
  status: 'done' | 'pending' | 'warning' | 'error';
}

interface StatePanelAction {
  label: string;
  onClick: () => void;
  variant?: VariantProps<typeof buttonVariants>['variant'];
  disabled?: boolean;
  loading?: boolean;
}

interface StatePanelStatus {
  label: string;
  tone?: StatusTone;
}

type StatePanelTone = StatusTone | 'brand';

interface StatePanelProps {
  icon?: React.ReactNode;
  title: string;
  description: string;
  /**
   * Mono-caps line above the title naming the situation ("Account
   * mismatch", "Wallet session"). Falls back to `status.label`.
   */
  eyebrow?: string;
  /** Tints the icon emblem. Falls back to `status.tone`. */
  tone?: StatePanelTone;
  /**
   * Legacy alias: renders as the eyebrow + emblem tone. Kept so existing
   * callers converge on the one approved anatomy without churn.
   */
  status?: StatePanelStatus;
  /** Optional checklist, e.g. Reports' "fix before finalizing" items. */
  items?: StatePanelItem[];
  /** The next action, whenever one exists. Pure waiting states omit it. */
  action?: StatePanelAction;
  secondaryAction?: StatePanelAction;
  /**
   * Short machine-readable failure detail (e.g. an API error code and request
   * id) shown under the actions. For support and triage — never the only
   * explanation of what went wrong; `description` still carries that.
   */
  diagnostic?: string;
  className?: string;
}

const emblemToneClassName: Record<StatePanelTone, string> = {
  neutral: 'bg-rowa-surface-inset text-rowa-text-primary',
  brand: 'bg-rowa-brand-mint/50 text-rowa-text-brand',
  positive: 'bg-rowa-status-positive/12 text-rowa-status-positive',
  warning: 'bg-rowa-status-warning/15 text-rowa-status-warning',
  error: 'bg-rowa-status-error/12 text-rowa-status-error'
};

const itemStatusIcon: Record<StatePanelItem['status'], React.ReactNode> = {
  done: <CheckCircle2 className="text-rowa-status-positive h-4 w-4 shrink-0" aria-hidden />,
  pending: <Circle className="text-rowa-text-secondary h-4 w-4 shrink-0" aria-hidden />,
  warning: <AlertTriangle className="text-rowa-status-warning h-4 w-4 shrink-0" aria-hidden />,
  error: <AlertCircle className="text-rowa-status-error h-4 w-4 shrink-0" aria-hidden />
};

/**
 * Informative-empty-state panel for a screen with nothing to show right
 * now but a clear next step — Mandate's "No changes in progress", Reports'
 * "Not ready" checklist, Wallet & Access's empty access list. Distinct from
 * `EmptyState` (whose action is optional, for plain "nothing here yet"
 * lists): a `StatePanel` always explains the current state and always
 * offers the next action.
 */
function StatePanel({
  icon,
  title,
  description,
  eyebrow,
  tone,
  status,
  items,
  action,
  secondaryAction,
  diagnostic,
  className
}: StatePanelProps) {
  const eyebrowText = eyebrow ?? status?.label;
  const emblemTone = tone ?? status?.tone ?? 'neutral';

  return (
    <LiquidSurface type="panel" data-slot="state-panel" className={cn('p-8', className)}>
      <Empty className="border-none p-0">
        {icon && (
          <EmptyMedia
            variant="icon"
            data-tone={emblemTone}
            className={cn(
              'rounded-rowa-md size-14 [&_svg]:size-6',
              emblemToneClassName[emblemTone]
            )}
          >
            {icon}
          </EmptyMedia>
        )}
        <EmptyHeader>
          {eyebrowText && (
            <div
              data-slot="state-panel-eyebrow"
              className="text-rowa-text-tertiary font-mono text-[11px] font-medium tracking-[0.18em] uppercase"
            >
              {eyebrowText}
            </div>
          )}
          <EmptyTitle className="text-rowa-card" role="heading" aria-level={2}>
            {title}
          </EmptyTitle>
          <EmptyDescription className="text-rowa-body-md">{description}</EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          {items && items.length > 0 && (
            <ul className="w-full space-y-2">
              {items.map((item) => (
                <li key={item.label} className="flex items-center gap-2 text-left">
                  {itemStatusIcon[item.status]}
                  <span
                    className={cn(
                      'text-rowa-body-sm',
                      item.status === 'error'
                        ? 'text-rowa-status-error'
                        : item.status === 'warning'
                          ? 'text-rowa-status-warning'
                          : 'text-rowa-text-secondary'
                    )}
                  >
                    {item.label}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {(action || secondaryAction) && (
            <div className="flex flex-wrap items-center justify-center gap-2">
              {action && (
                <Button
                  variant={action.variant}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  loading={action.loading}
                >
                  {action.label}
                </Button>
              )}
              {secondaryAction && (
                <Button
                  variant={secondaryAction.variant ?? 'quiet'}
                  onClick={secondaryAction.onClick}
                  disabled={secondaryAction.disabled}
                  loading={secondaryAction.loading}
                >
                  {secondaryAction.label}
                </Button>
              )}
            </div>
          )}
          {diagnostic && (
            <p
              data-slot="state-panel-diagnostic"
              className="text-rowa-label-sm text-rowa-text-tertiary mt-3 font-mono break-all"
            >
              {diagnostic}
            </p>
          )}
        </EmptyContent>
      </Empty>
    </LiquidSurface>
  );
}

export { StatePanel };
export type { StatePanelAction, StatePanelItem, StatePanelProps, StatePanelStatus };
